import React from 'react';

import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import {
  getOauthErrorHelpMessage,
  getOAuthErrorMessage,
  useCanvaAuthorize,
} from 'hooks/canvaAuth/useCanvaAuthorize';
import Button from 'components/base/Button';
import { ArrowLeft } from 'components/base/SVG';
import Rocket from 'res/img/Rocket.png';
import Lookup from 'res/img/Lookup.png';
import { EMPLOYER_ROUTES } from 'consts';

import {
  Container,
  Content,
  ErrorMessage,
  HelpText,
  LoadingText,
  LookupImage,
  RocketImage,
  Spinner,
  SuccessTitle,
} from './authorize.oauth.style';

// Error Display Component
interface ErrorDisplayProps {
  error: any;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBackToLogin = () => {
    // I'm sure that we able to get back /employer/canva/authorize after login because sessionStorage of "canva_url" still in place
    navigate(`${EMPLOYER_ROUTES.LOGIN}?autoRedirect=false`);
  };

  const errorMessage = getOAuthErrorMessage(error.code || 'unknown_error');
  const errorDescription = getOauthErrorHelpMessage(
    error.code || 'unknown_error',
  );

  return (
    <Container>
      <Content>
        <LookupImage src={Lookup} />
        <ErrorMessage>{errorMessage}</ErrorMessage>
        <HelpText>{errorDescription}</HelpText>
        <Button
          onClick={handleBackToLogin}
          text={t('BackToLogin')}
          textVariant={'canvaBtn'}
          size={'sm'}
          icon={<ArrowLeft />}
          iconPosition={'before'}
          style={{ flexGrow: 1, marginTop: 10 }}
        />
      </Content>
    </Container>
  );
};

// Main Component
export const CanvaAuthorizePage = () => {
  const { t } = useTranslation();
  const { data, isLoading, error, isError } = useCanvaAuthorize();

  // Loading state
  if (isLoading) {
    return (
      <Container>
        <Content>
          <Spinner />
          <LoadingText>{t('CanvaAuthenticating')}</LoadingText>
        </Content>
      </Container>
    );
  }

  // Error state
  if (isError || error) {
    return <ErrorDisplay error={error} />;
  }

  // Success state (shown briefly before redirect)
  if (data) {
    return (
      <Container>
        <Content>
          <RocketImage src={Rocket} />
          <SuccessTitle>{t('CanvaLoggedInSuccessfully')}</SuccessTitle>
          <HelpText>{t('CanvaTakingYouBack')}</HelpText>
        </Content>
      </Container>
    );
  }

  return null;
};
